/* LEGEND — Momentum Vision detection layer (rules-v0).
   A canvas glued to the ChartPane's time & price axes, drawing the
   vision-service's frames: structure boxes, glyphs, engine
   pills, touch-tested level lamps, a hover popover and a corner status
   readout. The layer OWNS its hydrate+subscribe (RrCard/#586 rule): frames
   re-render only this component, never the Terminal tree.

   Lane: MV-5 (`vision-mv5:{SYM}`), the only lane since MV-4 was retired on
   2026-10-25 (lib/vision-lane.ts). `laneLabel: false` leaves the lane name off
   the status badge (the teaching replay, whose recorded frames have no lanes).

   PERFORMANCE CONTRACT (the quote-flood / #586 lessons, enforced here):
   - The rAF loop is a PAINT GOVERNOR, not a painter: each tick computes a
     cheap dirty key (frame seq, hover, chart size/DPR, visible range, a
     price-scale probe, stale bucket, tf) and SKIPS the paint entirely when
     nothing changed. A static chart with a static frame costs ~a string
     compare per tick — no clearRect, no draw.
   - Marching-dash animation (forming boxes only) repaints at ≤30fps, and only
     while a forming box exists.
   - document.hidden pauses everything.
   - The paint itself lives in vision-draw.ts (pure, measureText-cached,
     benchmarked by scripts/vision-bench.cjs); worst-case JS cost is
     microseconds — see the bench numbers in the PR.
   - Live counters at window.__visionPerf (+ the status badge's title).

   v0 honesty: frames carry real STATES and measured FACTS — no probabilities.
   Contract: architecture/momentum-vision/vision/display.md (+ build.md §3). */
import React from 'react';
import { getVision, subscribeVision } from '../data/source';
import { etWhen, READ_LIVE_MS, readState } from '../lib/vision-read';
import { modelLabel } from '../lib/vision-model';
import { LANE_LABEL } from '../lib/vision-lane';
import { useVisionLane } from '../lib/use-vision-lane';
import { drawVisionFrame, hasForming, tfMinutes, vColor, vLabel } from './vision-draw';

const { useRef: useRefV, useEffect: useEffectV, useState: useStateV } = React;
const h = React.createElement;

// Past READ_LIVE_MS (2.5 min) the read is the LAST read: drawn faded and labelled with its time, never cleared — a
// halt, a quiet stock or the hours MV-5 does not score bring no new frame, and the read from before still stands
// (lib/vision-read.ts). It used to clear at 6 min, and a hydrate older than 2 min was dropped ("MV-5 no feed").
const STALE_MS = READ_LIVE_MS;
const ANIM_MIN_INTERVAL_MS = 33; // forming-dash animation at ≤30fps

export function VisionLayer(props: any) {
  const { chartApiRef, symbol, tf } = props;
  const cvRef = useRefV<any>(null);
  const rectsRef = useRefV<any[]>([]);
  const hoverRef = useRefV<string | null>(null);
  const [hover, setHover] = useStateV<any>(null);
  const [frame, setFrame] = useStateV<any>(null);
  // Vision lane (lib/vision-lane.ts): which publisher this layer draws — MV-5,
  // the only lane since MV-4 was retired.
  const lane = useVisionLane();
  // The hydrate for the current symbol+lane has answered — until then a
  // missing frame is "loading", after it "no feed" (said, never papered over).
  const [hydrated, setHydrated] = useStateV(false);
  const stRef = useRefV<any>({});
  stRef.current = { frame, tf, lane };
  const perfRef = useRefV<any>({ paints: 0, skips: 0, msEma: 0, msMax: 0, lastKey: '', lastPaintAt: 0, fadeKey: '', fadeT: 0 });

  // Hydrate + live feed — the layer owns both, keyed on symbol AND lane (#586
  // pattern). setFrame(null) is also the seq/ts guard reset: the guard below
  // compares against `prev`, and the two lanes' publishers count seq apart.
  useEffectV(() => {
    let alive = true;
    setFrame(null); // never draw the previous symbol's (or lane's) structures
    setHydrated(false);
    getVision(symbol, lane).then((r: any) => {
      if (!alive) return;
      setHydrated(true);
      // the saved read whatever its age (realtime's `active` is "fresh", not "valid")
      if (r && r.frame) {
        setFrame((prev: any) => (prev && prev.seq >= r.frame.seq ? prev : r.frame));
      }
    }).catch(() => { if (alive) setHydrated(true); });
    const off = subscribeVision(symbol, (fr: any) => {
      if (!alive || !fr) return;
      setFrame((prev: any) => {
        // seq resets on service respawn — accept older seq only when ts is newer (RR rule).
        if (prev && fr.seq < prev.seq && fr.ts <= prev.ts + 5000) return prev;
        return fr;
      });
    }, lane);
    return () => { alive = false; off(); };
  }, [symbol, lane]);

  // Staleness heartbeat for the status readout (the canvas has its own governor).
  const [, force] = useStateV(0);
  useEffectV(() => {
    const iv = setInterval(() => force((x: number) => x + 1), 5000);
    return () => clearInterval(iv);
  }, []);

  // rAF paint governor + painter.
  useEffectV(() => {
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (typeof document !== 'undefined' && document.hidden) return; // hidden tab: zero work
      const cv = cvRef.current, api = chartApiRef.current;
      if (!cv || !api || !api.chart) return;
      const P = perfRef.current;
      const fr = stRef.current.frame;
      const host = cv.parentElement, w = host.clientWidth, hh = host.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      const now = Date.now();
      const age = fr ? now - fr.ts : Infinity;
      const dead = !fr || fr.warm === false;

      // ---- dirty key: cheap probes; unchanged key ⇒ skip the whole paint ----
      let key: string;
      let range: any = null;
      if (dead) {
        key = 'dead:' + w + 'x' + hh;
      } else {
        try { range = api.chart.timeScale().getVisibleLogicalRange(); } catch { range = null; }
        // price-scale probe: box Y coords move on autoscale even when the frame
        // and range do not — probe the frame's price extremes through priceToY.
        const yLo = api.priceToY(fr._pLo != null ? fr._pLo : probeLo(fr));
        const yHi = api.priceToY(fr._pHi != null ? fr._pHi : probeHi(fr));
        // lane first: the two publishers' seq can coincide for one symbol
        key = stRef.current.lane + '|' + fr.symbol + ':' + fr.seq + '|' + (hoverRef.current || '') + '|' + w + 'x' + hh + ':' + dpr +
          '|' + (range ? range.from.toFixed(1) + ':' + range.to.toFixed(1) : 'n') +
          '|' + (yLo == null ? 'n' : Math.round(yLo)) + ':' + (yHi == null ? 'n' : Math.round(yHi)) +
          '|' + Math.floor(age / 10_000) + '|' + stRef.current.tf;
      }
      const animated = !dead && hasForming(fr);
      if (key === P.lastKey && (!animated || now - P.lastPaintAt < ANIM_MIN_INTERVAL_MS)) {
        P.skips++;
        return;
      }
      P.lastKey = key;
      P.lastPaintAt = now;

      // ---- paint ----
      const t0 = performance.now();
      if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(hh * dpr)) {
        cv.width = Math.round(w * dpr); cv.height = Math.round(hh * dpr);
        cv.style.width = w + 'px'; cv.style.height = hh + 'px';
      }
      const ctx = cv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, hh);
      rectsRef.current = [];
      if (!dead) {
        const fadeKey = stRef.current.lane + '|' + fr.symbol;
        if (P.fadeKey !== fadeKey) { P.fadeKey = fadeKey; P.fadeT = t0; }
        const fadeA = Math.min(1, (t0 - P.fadeT) / 300);
        const alpha = fadeA * (age > STALE_MS ? 0.4 : 1);
        const ts = api.chart.timeScale();
        const chartTfSec = tfMinutes(stRef.current.tf) * 60;
        const coord = {
          X: (ms: number) => {
            const sec = Math.floor(ms / 1000 / chartTfSec) * chartTfSec;
            const c = ts.timeToCoordinate(sec);
            return c == null ? null : c;
          },
          Y: (p: number) => api.priceToY(p),
          // Inverse mapping: lets the draw code pin an off-scale marker to the
          // edge it fell off instead of dropping it silently (see edgeY).
          yToPrice: (y: number) => api.yToPrice(y),
          rightX: w - 64,
        };
        try {
          const out = drawVisionFrame(ctx, w, hh, coord, fr, { hoverId: hoverRef.current, alpha, nowMs: t0, wallMs: now });
          rectsRef.current = out.rects;
        } catch { /* one bad frame ≠ dead layer */ }
        if (fadeA < 1) P.lastKey = ''; // fade-in still animating — repaint next tick
      }
      const ms = performance.now() - t0;
      P.paints++;
      P.msEma = P.msEma === 0 ? ms : P.msEma * 0.9 + ms * 0.1;
      if (ms > P.msMax) P.msMax = ms;
      (window as any).__visionPerf = { paints: P.paints, skips: P.skips, msEma: +P.msEma.toFixed(3), msMax: +P.msMax.toFixed(2) };
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Hover hit-test on the stage (one listener, ≤24 rect tests per move).
  useEffectV(() => {
    const cv = cvRef.current; if (!cv) return;
    const stage = cv.parentElement;
    const move = (e: any) => {
      const rect = cv.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const rs = rectsRef.current;
      let hit: any = null;
      for (let i = rs.length - 1; i >= 0; i--) {
        const r = rs[i];
        if (mx >= r.x && mx <= r.x + r.w && my >= r.y && my <= r.y + r.h) { hit = { o: r.o, bx: r.x, by: r.y }; break; }
      }
      const id = hit ? hit.o.id : null;
      if (id !== hoverRef.current) { hoverRef.current = id; setHover(hit); }
    };
    const leave = () => { hoverRef.current = null; setHover(null); };
    stage.addEventListener('mousemove', move);
    stage.addEventListener('mouseleave', leave);
    return () => { stage.removeEventListener('mousemove', move); stage.removeEventListener('mouseleave', leave); };
  }, []);

  const age = frame ? Date.now() - frame.ts : Infinity;
  const warm = frame && frame.warm !== false;
  let vstate = 'live', vcls = '';
  // A lane with nothing to show says so by name ("MV-5 no feed").
  if (!frame) { vstate = hydrated ? 'no feed' : 'loading'; vcls = hydrated ? 'bad' : 'warn'; }
  else if (!warm) { vstate = 'warming'; vcls = 'warn'; }
  else if (readState(frame.ts, Date.now()) === 'last') { vstate = 'last read'; vcls = 'warn'; }
  const forming = frame && warm ? (frame.forming || 0) : 0;
  const P = perfRef.current;
  const named = props.laneLabel !== false;

  const status = h('div', {
    className: 'v-status ' + vcls,
    // Perf receipts for the operator: hover the badge (also window.__visionPerf).
    title: 'paints ' + P.paints + ' · skipped ' + P.skips + ' · draw ~' + P.msEma.toFixed(2) + 'ms (max ' + P.msMax.toFixed(1) + 'ms)',
  },
    h('span', { className: 'vs-badge' }, h('span', { className: 'd' }),
      vstate === 'live' ? 'Vision' : named ? LANE_LABEL[lane] + ' ' + vstate : vstate),
    frame ? h('span', { className: 'vs-sep' }) : null,
    frame ? h('span', { className: 'vs-k' }, 'model') : null,
    frame ? h('span', {
      className: 'vs-v vs-model',
      title: (frame.model ? frame.model.version + ' · ' : '') + ((frame.model && frame.model.engineCommit) || ''),
    }, frame.model ? modelLabel(frame.model.version) : '—') : null,
    // Tape-informed dot (MV-2 flow-live, frame contract §8): lit = this score
    // consumed live order-flow tensors; hollow = flow-masked (the seat's
    // 5-min flow baseline is still seeding, or quotes were stale >5s and the
    // scorer refused to guess). Absent entirely on silent/undecorated frames.
    frame && typeof (frame as any).flow === 'boolean' ? h(React.Fragment, null,
      h('span', { className: 'vs-k' }, 'tape'),
      h('span', {
        className: 'vs-flow ' + ((frame as any).flow ? 'on' : 'off'),
        title: (frame as any).flow
          ? 'tape-informed — live order-flow tensors in this score'
          : 'flow-masked — bars-only (seat flow baseline seeding, or quotes stale >5s)',
      }, '●')) : null,
    frame ? h('span', { className: 'vs-k' }, 'age') : null,
    frame ? h('span', { className: 'vs-v ' + (age > STALE_MS ? 'warn' : 'ok') }, age < 60_000 ? Math.round(age / 1000) + 's' : etWhen(frame.ts, Date.now())) : null,
    // MV-1's strongest current opinion — top class + calibrated p, always
    // shown even when nothing clears a marker threshold ("the model isn't
    // saying anything" on a ripper — it always says its best guess here).
    frame && (frame as any).mv1 && (frame as any).mv1.top && (frame as any).mv1.top.class ? h(React.Fragment, null,
      h('span', { className: 'vs-k' }, 'top'),
      h('span', {
        className: 'vs-v',
        style: { color: (frame as any).mv1.top.p >= 0.6 ? '#34d399' : (frame as any).mv1.top.p >= 0.4 ? '#e0b15a' : '#8b93a1' },
      }, Math.round((frame as any).mv1.top.p * 100) + '% ' +
         String((frame as any).mv1.top.class).replace('higher_low', 'HL').replace('lower_high', 'LH')
           .replace('selling_stopped', 'SS').replace('pullback', 'PB').replace('impulse', 'IMP').replace('base', 'BASE') +
         '·' + (frame as any).mv1.top.tf)) : null,
    forming ? h(React.Fragment, null,
      h('span', { className: 'vs-sep' }),
      h('span', { className: 'vs-v', style: { color: '#67e8f9' } }, forming + ' forming')) : null,
    frame && !warm ? h(React.Fragment, null,
      h('span', { className: 'vs-sep' }),
      h('span', { className: 'vs-v warn' }, 'engine warming up')) : null);

  return h('div', { className: 'v-layer' },
    h('canvas', { className: 'v-canvas', ref: cvRef }),
    status,
    hover ? h(VisionPopover, { hit: hover }) : null);
}

/* Frame price extremes for the autoscale probe — computed once per frame and
   memoized ON the frame object (frames are replaced wholesale, never mutated). */
function probeLo(fr: any): number {
  let lo = Infinity;
  for (const o of fr.objects || []) if (o.priceLow < lo) lo = o.priceLow;
  if (!Number.isFinite(lo)) lo = 1;
  fr._pLo = lo;
  return lo;
}
function probeHi(fr: any): number {
  let hi = 0;
  for (const o of fr.objects || []) if (o.priceHigh > hi) hi = o.priceHigh;
  if (hi <= 0) hi = 1;
  fr._pHi = hi;
  return hi;
}

function VisionPopover(props: any) {
  const o = props.hit.o;
  // Level pill hover: name the level and spell out each timeframe's verdict —
  // this popover IS the legend for the [FAM][1][3][5][15][n/m] pills.
  if (o.kind === 'level') {
    const lv = o.level;
    const left = Math.max(6, props.hit.bx), top = Math.max(6, props.hit.by - 8);
    const style: any = { left: left + 'px', top: top + 'px', transform: 'translateY(-100%)', '--vc': '#8ee6c0' };
    const WORD: Record<string, string> = { holding: 'holding', held: 'held', failed: 'failed', untested: 'untested' };
    const rows = ['1m', '3m', '5m', '15m'].map((k) => {
      const s = (lv.perTf || {})[k];
      const st = s ? s.status : 'untested';
      const detail = s && st === 'holding' ? ' ' + s.barsHeld + '/' + s.hBars + ' bars' : '';
      return h('div', { className: 'vp-row', key: k },
        h('span', { className: 'k' }, k),
        h('span', { className: 'v', style: { color: st === 'failed' ? '#ec6a6a' : st === 'untested' ? undefined : '#8ee6c0' } },
          (WORD[st] || st) + detail));
    });
    return h('div', { className: 'v-pop', style },
      h('div', { className: 'vp-head' },
        h('span', { className: 'vp-cls' }, (typeof lv.type === 'string' && lv.type.includes('_')
          ? lv.type.split('_').slice(1).join('_').toUpperCase() + '\u00b7' + lv.type.split('_')[0]
          : lv.family) + ' ' + lv.side),
        h('span', { className: 'vp-state confirmed' }, '$' + lv.price)),
      rows,
      h('div', { className: 'vp-row' },
        h('span', { className: 'k' }, 'supported'),
        h('span', { className: 'v' }, lv.supported + ' of tested timeframes holding')));
  }
  const col = vColor(o.class);
  const left = Math.max(6, props.hit.bx), top = Math.max(6, props.hit.by - 8);
  const style: any = { left: left + 'px', top: top + 'px', transform: 'translateY(-100%)', '--vc': col };
  const facts = o.facts || {};
  const factRows = Object.keys(facts).filter((k) => k !== 'impulseId').map((k) =>
    h('div', { className: 'vp-row', key: k },
      h('span', { className: 'k' }, k.replace(/([A-Z])/g, ' $1').toLowerCase()),
      h('span', { className: 'v' }, String(facts[k]))));
  const lamps = h('span', { className: 'tf-lamps' }, ['1m', '3m', '5m', '15m'].map((k, i) => {
    const lit = (o.tfs || []).indexOf(k) >= 0;
    return h('span', { key: k, className: 'tf-lamp' + (lit ? ' lit hold' : '') }, h('span', null, ['1', '3', '5', '15'][i]));
  }));
  return h('div', { className: 'v-pop', style },
    h('div', { className: 'vp-head' },
      h('span', { className: 'vp-cls' }, vLabel(o.class)),
      h('span', { className: 'vp-state ' + o.state }, o.state)),
    h('div', { className: 'vp-row' },
      h('span', { className: 'k' }, o.source === 'engine' ? 'engine · ' + o.engine : 'timeframes'),
      o.source === 'engine' ? h('span', { className: 'v' }, (o.tfs || []).join(' ')) : lamps),
    factRows,
    o.anchor && (o.anchor.family || o.anchor.levelId)
      ? h('div', { className: 'vp-row' },
          h('span', { className: 'k' }, 'anchor'),
          h('span', { className: 'v' }, (o.anchor.family || o.anchor.levelId) + ' ' + (o.anchor.side || '')))
      : null,
    o.veto ? h('div', { className: 'vp-tags' }, h('span', { className: 'vp-tag veto' }, 'LONG VETO')) : null);
}
